/* istanbul ignore file */

import React, { memo, useEffect } from 'react';
import type { ImageSourcePropType } from 'react-native';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  Image,
  theme,
  TitleText,
  useNoticeBannersStackContext,
  useResponsive,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsListInfoMessage = memo((props: Props) => {
  const isLocationSelectionUpdateEnabled = useFeatureFlag('CELS-5577-order-location-selection-ui-update-enabled'); // prettier-ignore

  if (!isLocationSelectionUpdateEnabled) {
    return <LocationResultsListInfoMessageLegacy {...props} />;
  }

  return <LocationResultsListInfoMessageInline {...props} />;
});

export const LocationResultsListInfoMessageInline = memo((props: Props) => {
  const { title, text, illustration, illustrationAriaLabel } = props;

  const { match } = useResponsive();

  const isLocationSelectionUpdateEnabled = useFeatureFlag('CELS-5577-order-location-selection-ui-update-enabled'); // prettier-ignore

  // ─── Styles ────────────────────────────────────────────────────────────────

  const centeredContainerStyle = isLocationSelectionUpdateEnabled
    ? match([styles.containerXS, undefined])
    : undefined;
  const containerStyles = [styles.container, centeredContainerStyle];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <Image
        source={illustration}
        style={styles.illustration}
        aria-label={illustrationAriaLabel}
        contentFit="contain"
      />

      {title ? (
        <TitleText size={6} style={styles.title}>
          {title}
        </TitleText>
      ) : null}

      {text ? (
        <BodyText size={3} style={styles.text}>
          {text}
        </BodyText>
      ) : null}
    </View>
  );
});

// ─────────────────────────────────────────────────────────────────────────────

const LocationResultsListInfoMessageLegacy = (props: Props) => {
  const { match } = useResponsive();

  const ResponsiveComponent = match([
    LocationResultsListInfoMessageBanner,
    LocationResultsListInfoMessageInline,
  ]);

  return <ResponsiveComponent {...props} />;
};

const LocationResultsListInfoMessageBanner = (props: Props) => {
  const { title = '', text = '' } = props;

  const { push: addBanner, remove: removeBanner } = useNoticeBannersStackContext();

  const message = `${title} ${text}`.trim();

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    addBanner({ text: message, palette: 'neutral', id: 'location-result-info-message' }, true);

    return () => {
      removeBanner('location-result-info-message');
    };
  }, [addBanner, removeBanner, message]);

  return null;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingVertical: theme.spacing['6'],
    paddingHorizontal: theme.spacing['2'],
  },
  containerXS: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingBottom: theme.spacing['10'],
  },
  illustration: {
    width: 240,
    height: 240,
  },
  title: {
    textAlign: 'center',
    marginBottom: theme.spacing['4'],
  },
  text: {
    textAlign: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  illustration: ImageSourcePropType;
  illustrationAriaLabel: string;
  title?: string;
  text?: string;
};
